import React from 'react';
import { DownloadIcon } from './Icons';
import type { ExportEntry, ExportsData } from '../hooks/useExports';

/* ── App ID → display name mapping ── */
const APP_NAMES: Record<string, string> = {
  mockgen: 'MockGen',
  stylefinder: 'StyleFinder',
  reframe: 'Reframe',
};

function appLabel(appId: string): string {
  return APP_NAMES[appId] || appId;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface MyActivityProps {
  exports: ExportsData;
  isLoading: boolean;
}

export const MyActivitySection: React.FC<MyActivityProps> = ({ exports: data, isLoading }) => {
  const barWidth = data.exportsMax > 0
    ? `${(data.exportsUsed / data.exportsMax) * 100}%`
    : '0%';
  const remaining = data.exportsMax - data.exportsUsed;

  return (
    <section id="my-activity" className="logged-only fs-page" style={{ paddingTop: 64, paddingBottom: 96 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <p className="fs-section-label" style={{ marginBottom: 8 }}>My Activity</p>
          <h2 style={{
            fontFamily: 'var(--fs-font-headline)',
            fontWeight: 700,
            fontSize: 'clamp(24px, 3vw, 30px)',
            color: 'var(--fs-text)',
            letterSpacing: '-0.02em',
          }}>Your workspace</h2>
        </div>
        <button className="fs-btn-ghost" style={{ fontSize: 12, padding: '8px 16px' }}>View all history</button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 32 }}>
        {/* Exports this week */}
        <div className="fs-stat-card">
          <p className="fs-stat-label">Exports this week</p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <span className="fs-stat-value" style={{ color: 'var(--fs-cyan)' }}>{data.exportsUsed}</span>
            <span style={{ fontFamily: 'var(--fs-font-headline)', fontWeight: 700, fontSize: 18, color: 'var(--fs-text-muted)', marginBottom: 2 }}>/ {data.exportsMax}</span>
          </div>
          <div className="fs-export-bar-track">
            <div className="fs-export-bar-fill" style={{ width: barWidth }} />
          </div>
          <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 12, color: 'var(--fs-text-disabled)', marginTop: 6 }}>
            {data.resetLabel} · {remaining} left this week
          </p>
        </div>

        {/* Most used app */}
        <div className="fs-stat-card">
          <p className="fs-stat-label">Most used app</p>
          <span className="fs-stat-value" style={{ fontSize: 22 }}>
            {data.mostUsedApp ? appLabel(data.mostUsedApp.appId) : '—'}
          </span>
          <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 12, color: 'var(--fs-text-disabled)', marginTop: 6 }}>
            {data.mostUsedApp ? `${data.mostUsedApp.count} exports total` : 'No exports yet'}
          </p>
        </div>

        {/* Last export */}
        <div className="fs-stat-card">
          <p className="fs-stat-label">Last export</p>
          <span className="fs-stat-value" style={{ fontSize: 22 }}>
            {data.lastExport ? timeAgo(data.lastExport.createdAt) : '—'}
          </span>
          <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 12, color: 'var(--fs-text-disabled)', marginTop: 6 }}>
            {data.lastExport
              ? `${data.lastExport.title} · ${appLabel(data.lastExport.appId)}`
              : 'Nothing yet'}
          </p>
          {data.lastExport && (
            <div style={{ marginTop: 12 }}>
              <button className="fs-btn-ghost" style={{ fontSize: 11, padding: '6px 12px' }}>Re-open project</button>
            </div>
          )}
        </div>
      </div>

      {/* Recent exports */}
      <div style={{ background: 'var(--fs-surface)', border: '1px solid var(--fs-border)', borderRadius: 'var(--fs-radius-card)', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <p style={{ fontFamily: 'var(--fs-font-headline)', fontWeight: 700, fontSize: 14, color: 'var(--fs-text)' }}>Recent exports</p>
          <span style={{ fontFamily: 'var(--fs-font-body)', fontSize: 11, color: 'var(--fs-text-muted)' }}>{data.recent.length} of {data.exportsUsed} this week</span>
        </div>

        {isLoading && data.recent.length === 0 && (
          <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 13, color: 'var(--fs-text-muted)', padding: '20px 0', textAlign: 'center' }}>Loading...</p>
        )}

        {!isLoading && data.recent.length === 0 && (
          <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 13, color: 'var(--fs-text-muted)', padding: '20px 0', textAlign: 'center' }}>No exports yet. Start creating!</p>
        )}

        {data.recent.map(exp => (
          <div key={exp.id} className="fs-export-row">
            <div className="fs-export-thumb">
              <div style={{ width: '100%', height: '100%', background: 'var(--fs-surface-hi)' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 13, fontWeight: 600, color: 'var(--fs-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {exp.title}
              </p>
              <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 11, color: 'var(--fs-text-muted)', marginTop: 2 }}>
                {appLabel(exp.appId)} · {timeAgo(exp.createdAt)}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{ fontFamily: 'var(--fs-font-body)', fontSize: 10, color: 'var(--fs-text-muted)', background: 'var(--fs-surface-low)', padding: '4px 8px', borderRadius: 4 }}>
                {exp.status}
              </span>
              <button className="fs-export-dl-btn" title="Download">
                <DownloadIcon />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
